import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AppComponent } from '../../app.component';
import { BaseService } from '../../shared/services/base.service';

import { WorkOrder } from '../../shared/models/work.order.interface';
import { WorkOrderType } from '../../shared/models/work.order.type.interface';
import { Crew } from '../../shared/models/crew.interface';
import { Resource } from '../../shared/models/resource.interface';
import { ResourceEdit } from '../models/resource.edit.interface';
import { Customer } from '../../shared/models/customer.interface';
import { User } from '../../shared/models/user.interface';
import { WorkOrderFilter } from '../models/work.order.filter';
import { DealerWorkOrderSummary, ResourceTypeEdit } from '../list/list.component';
import { FieldTechnician } from '../../shared/models/field.technician.interface';
import { LaborEdit } from '../models/labor.edit.interface';
import { Appointment } from '../../shared/models/appointment.interface';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';

@Injectable()

export class WorkOrderService extends BaseService {

  constructor(private datepipe: DatePipe, private appComponent: AppComponent, protected http: HttpClient, router: Router) {
    super(http, router);
  }

  getSummary(filter: WorkOrderFilter): Observable<DealerWorkOrderSummary> {
    let params = new HttpParams();

    let startDate = this.datepipe.transform(filter.startDate, 'yyyy-MM-ddT00:00:00');
    let endDate = this.datepipe.transform(filter.endDate, 'yyyy-MM-ddT00:00:00');

    if (filter.startDate != null) params = params.append('startDate', startDate);
    if (filter.endDate != null) params = params.append('endDate', endDate);
    if (filter.projectName) params = params.append('projectName', filter.projectName);
    if (filter.customerId) params = params.append('customerId', filter.customerId.toString());
    if (filter.jobTypeId) params = params.append('jobTypeId', filter.jobTypeId.toString());
    if (filter.crew) params = params.append('crewId', filter.crew.crew_ID.toString());

    return this.httpGet<DealerWorkOrderSummary>("/workOrder/summary?" + params.toString());
  }

  getList(filter: WorkOrderFilter, sortColumn: string, sortDirection: string, page: number): Observable<WorkOrder[]> {
    let params = new HttpParams();

    let startDate = this.datepipe.transform(filter.startDate, 'yyyy-MM-ddT00:00:00');
    let endDate = this.datepipe.transform(filter.endDate, 'yyyy-MM-ddT00:00:00');

    if (page != null) params = params.append('page', page.toString());
    if (sortColumn != null) params = params.append('sortColumn', sortColumn);
    if (sortDirection != null) params = params.append('sortDirection', sortDirection);
    if (filter.startDate != null) params = params.append('startDate', startDate);
    if (filter.endDate != null) params = params.append('endDate', endDate);
    if (filter.projectName) params = params.append('projectName', filter.projectName);
    if (filter.customerId) params = params.append('customerId', filter.customerId.toString());
    if (filter.jobTypeId) params = params.append('jobTypeId', filter.jobTypeId.toString());
    if (filter.crew) params = params.append('crewId', filter.crew.crew_ID.toString());

    return this.httpGet<WorkOrder[]>("/workOrder?" + params.toString());
  }

  getCalendar(): Observable<WorkOrder[]> {
    return this.httpGet<WorkOrder[]>("/workOrder/calendar");
  }

  saveWorkOrder(workOrderId: number, workOrder: WorkOrder): Observable<number> {
    return this.httpPost<number>("/workOrder/" + workOrderId, workOrder);
  }

  saveAppointment(appointmentId: number, appointment: Appointment): Observable<number> {
    if (appointmentId)
      return this.httpPost<number>("/workOrder/appointment/" + appointmentId, appointment); 
    else
      return this.httpPut<number>("/workOrder/appointment", appointment);
  }

  deleteAppointment(appointmentId: number): Observable<number> {
    return this.httpDelete<number>("/workOrder/appointment/" + appointmentId);
  }

  saveResources(workOrderId: number, resources: ResourceEdit[]): Observable<number> {
    let update = {
      workOrderResources: resources
    };

    return this.httpPost<number>("/workOrder/resource/" + workOrderId, update);
  }

  saveLabor(workOrderId: number, laborEdit: LaborEdit): Observable<number> {
    return this.httpPost<number>("/workOrder/labor/" + workOrderId, laborEdit);
  }

  saveCrew(workOrderId: number, removals: number[], additions: number[]): Observable<number> {
    let update = {
      removals: removals,
      additions: additions
    };

    return this.httpPost<number>("/workOrder/crew/" + workOrderId, update);
  }

  getWorkOrderTypes(): Observable<WorkOrderType[]> {
    return this.httpGet<WorkOrderType[]>("/workOrder/workOrderType");
  }

  getCustomers(): Observable<Customer[]> {
    return this.httpGet<Customer[]>("/customer");
  }

  getSalespersons(): Observable<User[]> {
    return this.httpGet<User[]>("/user/salesperson");
  }

  getFieldTechs(): Observable<FieldTechnician[]> {
    return this.httpGet<FieldTechnician[]>("/crew/fieldTechs");
  }

  getCrews(): Observable<Crew[]> {
    return this.httpGet<Crew[]>("/workOrder/crew");
  }

  getResources(): Observable<Resource[]> {
    return this.httpGet<Resource[]>("/workOrder/resource");
  }

  printWorkOrder(workOrderId: number, showPricing: boolean, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('timezone', timezone.toString())
      .append('showPricing', showPricing.toString())
      .append('locale', locale);

    return this.httpGetFile("/report/workOrder/" + workOrderId + '?' + params.toString());
  }

  parseResourceTypes(workOrder: WorkOrder, resources: Resource[]): ResourceTypeEdit[] {
    let resourceEdits: ResourceEdit[] = new Array();
    for (let resource of resources) {
      let workOrderResource = workOrder.workOrderResources.find((wor) => {
        return wor.resource_ID == resource.resource_ID;
      });

      let resourceEdit: ResourceEdit = {
        resource_ID: resource.resource_ID,
        resource_Name: resource.resource_Name,
        resourceType: resource.resourceType
      } as ResourceEdit;

      if (workOrderResource != null) {
        resourceEdit.isSelected = true;
        resourceEdit.workOrderResource_Note = workOrderResource.workOrderResource_Note;
      }

      resourceEdits.push(resourceEdit);
    }

    let resourceGroups: Array<any> = resourceEdits.reduce(function (array, resource) {
      array[resource.resourceType.resourceType_Desc] = array[resource.resourceType.resourceType_Desc] || [];
      array[resource.resourceType.resourceType_Desc].push(resource);
      return array;
    }, Object.create(null));

    let resourceTypes: ResourceTypeEdit[] = new Array();
    for (let [key, value] of Object.entries(resourceGroups)) {
      resourceTypes.push({
        resourceType_Desc: key,
        resources: value
      });
    }


    return resourceTypes;
  }
}
